import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  TextField,
  Modal,
  Fade,
  Backdrop,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText
} from '@material-ui/core';
import PropTypes from 'prop-types';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { setOpenSnackbar } from '../../store/slices/snackbarSlice';
import { VMNetworkSettings } from '../rack/create/VMNetworkSettings';
import { VMDiskSettings } from '../rack/create/VMDiskSettings';
import { diskSizeMap } from '../rack/create/NewVMModal';
import { diskSizes } from '../rack/create/LRUSize';
import useVrackApi from '../../hooks/useVrackApi';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '100%',
  p: 4
};

const templateArchitectures = ['amd64', 'arm64'];
const maxReplicasOptions = 4;
const templateGlobals = [
  { label: 'Yes', value: true },
  { label: 'No', value: false }
];

const emptyTemplate = {
  label: '',
  architecture: '',
  max_replicas: '',
  network: {
    ports: [{ name: 'vm-p1' }]
  },
  description: '',
  disks: [
    {
      image: '',
      diskSize: `${diskSizeMap.small}G`,
      size: diskSizes[0]
    }
  ],
  name: '',
  provisioner_identity: '',
  size: 'small'
};

function hasValue(value) {
  return value.trim().length > 0;
}

NewTemplateModal.propTypes = {
  isOpen: PropTypes.bool,
  closeHandler: PropTypes.func,
  vmTemplates: PropTypes.array,
  getVMTemplateList: PropTypes.func
};

export default function NewTemplateModal({ isOpen, closeHandler, vmTemplates, getVMTemplateList }) {
  const { isAdmin } = useSelector((state) => state.userDetails.value);
  const [newTemplate, setNewTemplate] = useState(emptyTemplate);
  const [identities, setIdentities] = useState([]);
  const { fetchData: fetchIdentities } = useVrackApi('get', '/users/identities');
  const { fetchData: vrackApiCreateNewTemplate } = useVrackApi('post', '/racks/vm');

  const dispatch = useDispatch();
  const openSnackbar = (payload) => dispatch(setOpenSnackbar(payload));

  // sets the field value specified on the new template object
  const setField = (field) => (event) => {
    setNewTemplate((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const maxReplicasValue = Number(newTemplate.max_replicas);
  const maxReplicasValid = Number.isInteger(maxReplicasValue) && maxReplicasValue > 0;
  const hasAtLeastOnePort = newTemplate.network.ports.some((port) => port.name.trim().length > 0);
  const hasAtLeastOneDisk = newTemplate.disks.length > 0;
  const isValidDiskSize = useMemo(() => {
    const re = /^[0-9]+[GM]$/;
    return newTemplate.disks.every((disk) => {
      if (!re.test(disk.diskSize)) {
        return false;
      }

      const value = parseInt(disk.diskSize, 10);
      if (disk.diskSize.includes('G') && value > 2000) {
        return false;
      }
      if (disk.diskSize.includes('M') && value > 2000000) {
        return false;
      }
      return true;
    });
  }, [newTemplate.disks]);

  const formValid =
    hasValue(newTemplate.architecture) &&
    hasValue(newTemplate.label) &&
    maxReplicasValid &&
    hasValue(newTemplate.name) &&
    hasValue(newTemplate.provisioner_identity) &&
    hasValue(newTemplate.description) &&
    hasAtLeastOnePort &&
    hasAtLeastOneDisk &&
    isValidDiskSize;

  const getIdentities = async () => {
    await fetchIdentities()
      .then((response) => {
        console.debug('Identities were successfully retrieved from the API');
        setIdentities(response);
      })
      .catch((error) => {
        console.debug(error);
      });
  };

  useEffect(() => {
    getIdentities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async () => {
    vrackApiCreateNewTemplate({ data: newTemplate })
      .then(() => {
        openSnackbar({ message: 'Template created successfully!' });
        getVMTemplateList();
        closeHandler();
      })
      .catch(() => {
        openSnackbar({ message: 'Failed to create template', severity: 'error' });
      });
  };

  return (
    <Modal
      data-testid="new-template-modal"
      open={isOpen}
      sx={{ outline: 'none' }}
      onClose={closeHandler}
      aria-labelledby="new-template-modal"
      aria-describedby="modal to create a new VM template"
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={isOpen}>
        <Grid direction="row" sx={style} container>
          <Grid item xs={12} md={1} lg={2} onClick={closeHandler} />
          <Grid item xs={12} md={10} lg={8}>
            <Card>
              <CardHeader sx={{ padding: 2 }} title="New VM Template" />
              <Divider />
              <CardContent sx={{ paddingTop: 1, maxHeight: '70vh', overflow: 'auto' }}>
                <Box sx={{ width: '100%', marginTop: '10px' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        id="template-name"
                        label="Name"
                        placeholder="Template Name"
                        InputLabelProps={{ shrink: true }}
                        value={newTemplate.name}
                        onChange={setField('name')}
                        fullWidth
                        helperText="Unique VM template name"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        id="template-label"
                        label="Label"
                        placeholder="Template Label"
                        inputProps={{ 'data-testid': 'vm-template-label' }}
                        InputLabelProps={{ shrink: true }}
                        value={newTemplate.label}
                        onChange={setField('label')}
                        fullWidth
                        helperText="User friendly label for the VM Template"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth required>
                        <InputLabel id="template-provisioner-identity-label">
                          Team Identity
                        </InputLabel>
                        <Select
                          labelId="template-provisioner-identity-label"
                          id="template-provisioner-identity"
                          value={newTemplate.provisioner_identity}
                          label="Team Identity"
                          onChange={setField('provisioner_identity')}
                        >
                          {identities.length === 0 ? (
                            <MenuItem disabled value="">
                              No identities available
                            </MenuItem>
                          ) : (
                            identities.map((identityOption) => (
                              <MenuItem key={identityOption} value={identityOption}>
                                {identityOption}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </FormControl>
                      <FormHelperText>Select the provisioner identity</FormHelperText>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        id="template-description"
                        label="Description"
                        value={newTemplate.description}
                        onChange={setField('description')}
                        fullWidth
                        helperText="VM template description"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl
                        fullWidth
                        required
                        error={!maxReplicasValid && newTemplate.max_replicas !== ''}
                      >
                        <InputLabel id="template-max-replicas-label">Max Replicas</InputLabel>
                        <Select
                          labelId="template-max-replicas-label"
                          id="template-max-replicas"
                          value={newTemplate.max_replicas}
                          label="Max Replicas"
                          onChange={setField('max_replicas')}
                        >
                          {Array.from({ length: maxReplicasOptions }).map((_, count) => (
                            <MenuItem key={count + 1} value={count + 1}>
                              {count + 1}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          {!maxReplicasValid && newTemplate.max_replicas !== ''
                            ? 'Select a max replica count'
                            : 'Maximum number of replicas for this template'}
                        </FormHelperText>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth required>
                        <InputLabel id="template-architecture-label">Architecture</InputLabel>
                        <Select
                          labelId="template-architecture-label"
                          id="template-architecture"
                          value={newTemplate.architecture}
                          label="Architecture"
                          onChange={setField('architecture')}
                        >
                          {templateArchitectures.map((arch) => (
                            <MenuItem key={arch} value={arch}>
                              {arch}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormHelperText>The architecture for the template</FormHelperText>
                    </Grid>
                    {isAdmin && (
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth required>
                          <InputLabel id="template-global-label" required={false}>
                            Global
                          </InputLabel>

                          <Select
                            labelId="template-global-label"
                            id="template-global"
                            value={newTemplate.global}
                            label="Global"
                            onChange={setField('global')}
                          >
                            {templateGlobals.map((option) => (
                              <MenuItem key={option.value.toString()} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <FormHelperText>The global setting for the template</FormHelperText>
                      </Grid>
                    )}
                    <VMNetworkSettings currentVM={newTemplate} updateCurrentVM={setNewTemplate} />
                    <VMDiskSettings
                      currentVM={newTemplate}
                      updateCurrentVM={setNewTemplate}
                      vmTemplates={vmTemplates}
                    />
                  </Grid>
                </Box>
              </CardContent>
              <Divider />
              <Box sx={{ padding: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Box>
                  <Button
                    data-testid="vm-new-template-submit-btn"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleSubmit();
                    }}
                    color="primary"
                    variant="contained"
                    disabled={!formValid}
                  >
                    Submit
                  </Button>
                  <Button
                    data-testid="close-new-vm-template-modal"
                    sx={{ marginLeft: 1 }}
                    color="secondary"
                    variant="contained"
                    endIcon={<CloseOutlinedIcon />}
                    onClick={closeHandler}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} med={1} lg={3} onClick={closeHandler} />
        </Grid>
      </Fade>
    </Modal>
  );
}
